import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
// Import Types
import type { ABPreset } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { ABTestingHttpService } from 'services/http';
// Import UI components
import { BusyLoader, Empty, Tab, Tabs } from '@geneui/components';
// Import Config
import { parseToUIModel } from 'components/CampaignReport/CampaignReportABTest/config';
// Import Constants
import { l } from 'constants/common';
import CampaignABTestInfoModalContent from './CampaignABTestInfoModalContent';
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';

const CampaignABTestInfoModal = ({ campaignInfo, handleInfoModalActionClick }) => {
    const { ABTests: abTests, NextRunDate: campaignNextRunDate } = campaignInfo;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [promotionPresetsAPIModel, setPromotionPresetsAPIModel] = useState<Nullable<ABPreset[]>>(null);
    const [isLoadingPresets, setIsLoadingPresets] = useState(false);

    const { getPresetsRequest } = useCancelableRequests({
        getPresetsRequest: ABTestingHttpService.getPresets,
    });

    useEffect(() => {
        setIsLoadingPresets(true);
        doGetRequest(getPresetsRequest.request, {
            successCallback: (data) => setPromotionPresetsAPIModel(data),
        }).then(() => setIsLoadingPresets(false));
    }, [doGetRequest, getPresetsRequest.request]);

    const aBTestResultsUIModel = useMemo(() => {
        if (!isEmpty(abTests) && !isEmpty(promotionPresetsAPIModel)) {
            return parseToUIModel({
                testResultsAPI: abTests,
                promotionPresets: promotionPresetsAPIModel,
                t,
                // TODO: partnerDateFormat is needed, Please review later
                campaignNextRunDate,
            });
        }
        return [];
    }, [abTests, campaignNextRunDate, promotionPresetsAPIModel, t]);

    const handleStopABTest = useCallback(() => {
        handleInfoModalActionClick(modalsStateSetDictKeys.stopABTest, campaignInfo);
    }, [campaignInfo, handleInfoModalActionClick]);

    return (
        <BusyLoader isBusy={isLoadingPresets} type="spinner" spinnerSize="medium">
            <div className="crm-container-standard-padding">
                {isEmpty(aBTestResultsUIModel) ? (
                    <Empty title={t(l.NoDataToDisplay)} />
                ) : (
                    <Tabs type="box" position="top">
                        {aBTestResultsUIModel?.map(({ tabTitle, generalDetails }, index) => {
                            return (
                                <Tab key={index} title={tabTitle}>
                                    <CampaignABTestInfoModalContent
                                        generalDetails={generalDetails}
                                        handleStopABTest={handleStopABTest}
                                    />
                                </Tab>
                            );
                        })}
                    </Tabs>
                )}
            </div>
        </BusyLoader>
    );
};

CampaignABTestInfoModal.propTypes = {
    campaignInfo: PropTypes.object,
    handleInfoModalActionClick: PropTypes.func.isRequired,
};

export default CampaignABTestInfoModal;
